// Intraday stock chart: candles, VWAP, order-block zones, support/resistance, liquidity levels,
// sweeps, volume (split by estimated buy/sell) and the order-flow delta panel. Each overlay can be
// switched off, the time axis can be zoomed and panned, and a compact variant (candles, zones,
// levels, VWAP) is used in the board's hover card.
import * as d3 from 'd3';
import type { Analysed, Bar, Detail, Market, SRKey } from '@planb/core';
import { zoneLabel } from '@planb/core';
import { palette } from './palette';

// The theme's colours, refreshed on each draw (see charts/palette.ts).
let C = palette();

export type ChartLayer = 'vwap' | 'zones' | 'levels' | 'liquidity' | 'sweeps' | 'profile' | 'volume' | 'flow';
export const CHART_LAYERS: { id: ChartLayer; label: string }[] = [
  { id: 'vwap', label: 'VWAP' },
  { id: 'zones', label: 'Order blocks' },
  { id: 'levels', label: 'Support / resistance' },
  { id: 'liquidity', label: 'Liquidity' },
  { id: 'sweeps', label: 'Sweeps' },
  { id: 'profile', label: 'Volume profile' },
  { id: 'volume', label: 'Volume' },
  { id: 'flow', label: 'Order flow' },
];
export const ALL_LAYERS = new Set<ChartLayer>(CHART_LAYERS.map((l) => l.id));

/** Visible bar window [from, to) on the time axis; null shows the default span. */
export interface ChartView { from: number; to: number }

export interface ChartOptions {
  layers?: Set<ChartLayer>;
  view?: ChartView | null;
  /** Small card variant: price panel only, no interaction. */
  compact?: boolean;
  /** Zoom/pan requests (wheel with ⌘/Ctrl or a trackpad pinch, or any wheel when wheelZoom, and drag). */
  onView?: (v: ChartView | null) => void;
  wheelZoom?: boolean;
  /** Width/height of the space the chart fills (full screen); the price panel grows to fit. */
  aspect?: number;
}

export const fmtVol = (v: number) =>
  v >= 1e7 ? (v / 1e7).toFixed(2) + ' Cr' : v >= 1e5 ? (v / 1e5).toFixed(2) + ' L' : Math.round(v).toLocaleString('en-IN');

/**
 * Early in the day the x-axis covers the session so far plus some room, widening to the full day
 * as bars arrive, so a handful of candles isn't squeezed into a corner.
 */
export const spanFor = (market: Market, t: number) => (t >= market.bars * 0.8 ? market.bars : Math.min(market.bars, Math.max(24, t + 10)));

const MIN_BARS = 8;

/** Zoom the window by factor around bar position `at` (fractional index), clamped to [0, span]. */
export function zoomView(v: ChartView, factor: number, at: number, span: number): ChartView | null {
  const n = v.to - v.from;
  const m = Math.min(span, Math.max(MIN_BARS, Math.round(n * factor)));
  if (m >= span) return null;
  let from = Math.round(at - ((at - v.from) * m) / n);
  from = Math.max(0, Math.min(span - m, from));
  return { from, to: from + m };
}

export function panView(v: ChartView, by: number, span: number): ChartView {
  const n = v.to - v.from;
  const from = Math.max(0, Math.min(span - n, Math.round(v.from + by)));
  return { from, to: from + n };
}

let clipSeq = 0;

export function drawStockChart(svgEl: SVGSVGElement, market: Market, a: Analysed, d: Detail, t: number, opts: ChartOptions = {}) {
  C = palette();
  const fmtPx = market.formatPrice;
  const cur = market.currencySymbol;
  const compact = !!opts.compact;
  const on = (l: ChartLayer) => (opts.layers ?? ALL_LAYERS).has(l) && !(compact && (l === 'profile' || l === 'volume' || l === 'flow' || l === 'liquidity'));

  // Layout: the price panel, then the volume and delta panels when shown, then the time axis.
  const W = compact ? 420 : 760;
  const P = compact
    ? { x0: 6, x1: 342, y0: 10, y1: 178, v0: 0, v1: 0, d0: 0, d1: 0, bottom: 178, axis: 192 }
    : (() => {
      // Panels below the price chart take 30 (volume) and 62 (flow) units, the axis and readout 56.
      const below = (on('volume') ? 38 : 0) + (on('flow') ? 62 : 0) + 56;
      const y1 = opts.aspect ? Math.max(226, 760 / opts.aspect - below) : 226;
      const p = { x0: 46, x1: 668, y0: 14, y1, v0: 0, v1: 0, d0: 0, d1: 0, bottom: y1, axis: 0 };
      if (on('volume')) { p.v0 = p.bottom + 8; p.v1 = p.v0 + 30; p.bottom = p.v1; }
      if (on('flow')) { p.d0 = p.bottom + 10; p.d1 = p.d0 + 52; p.bottom = p.d1; }
      p.axis = p.bottom + 18;
      return p;
    })();
  const H = compact ? 200 : P.axis + 38;

  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();
  svg.on('wheel', null).on('mousedown', null);
  svg.attr('viewBox', `0 0 ${W} ${H}`);
  const span = spanFor(market, t);
  const view = opts.view && opts.view.to - opts.view.from < span ? opts.view : { from: 0, to: span };
  const zoomed = view.from > 0 || view.to < span;
  const slot = (P.x1 - P.x0) / (view.to - view.from);
  const xAt = (i: number) => P.x0 + (i - view.from + 0.5) * slot;
  const xEdge = (i: number) => P.x0 + (i - view.from) * slot;
  const bars = d.bars, lv = d.levels;
  const visible = bars.map((b, i) => [b, i] as const).filter(([, i]) => i >= view.from && i < view.to);

  // Price range: the whole day plus the levels drawn; when zoomed in, the visible candles (and
  // nearby levels) so the zoom actually magnifies price too.
  const liq = on('liquidity') ? d.liquidity.filter((l) => Math.abs(l.distPct) <= 2) : [];
  let lo: number, hi: number;
  if (zoomed && visible.length) {
    lo = Math.min(...visible.map(([b]) => b.l)); hi = Math.max(...visible.map(([b]) => b.h));
  } else {
    lo = a.low; hi = a.high;
    if (on('levels')) { lo = Math.min(lo, lv.S2); hi = Math.max(hi, lv.R2); }
    for (const l of liq) { lo = Math.min(lo, l.p); hi = Math.max(hi, l.p); }
  }
  const pad = (hi - lo) * 0.06 || a.ltp * 0.002;
  const y = d3.scaleLinear([lo - pad, hi + pad], [P.y1, P.y0]);
  const inPrice = (py: number) => py >= P.y0 - 1 && py <= P.y1 + 1;

  // Price drawings are clipped to the price panel; volume and delta to the whole plot width.
  const clipId = `dt-clip-${++clipSeq}`;
  const defs = svg.append('defs');
  defs.append('clipPath').attr('id', `${clipId}-p`)
    .append('rect').attr('x', P.x0).attr('y', P.y0 - 12).attr('width', P.x1 - P.x0).attr('height', P.y1 - P.y0 + 14);
  defs.append('clipPath').attr('id', `${clipId}-a`)
    .append('rect').attr('x', P.x0).attr('y', P.y0 - 2).attr('width', P.x1 - P.x0).attr('height', P.bottom - P.y0 + 2);
  const panel = svg.append('g').attr('clip-path', `url(#${clipId}-a)`);
  const plot = svg.append('g').attr('clip-path', `url(#${clipId}-p)`);

  // Grid + time axis
  y.ticks(compact ? 4 : 6).forEach((v) => {
    if (inPrice(y(v))) panel.append('line').attr('x1', P.x0).attr('x2', P.x1).attr('y1', y(v)).attr('y2', y(v)).attr('stroke', C.gridSoft);
  });
  const n = view.to - view.from;
  for (let i = view.from + 1; i <= view.to; i++) {
    const min = market.openMinute + i * market.barMinutes;
    const every = n <= 18 ? 15 : n <= 36 ? 30 : 60;
    if (!(min % every === 0 || (!zoomed && i === span))) continue;
    if (compact && min % 60 !== 0) continue;
    svg.append('line').attr('x1', xEdge(i)).attr('x2', xEdge(i)).attr('y1', P.y0).attr('y2', P.bottom).attr('stroke', C.gridSoft);
    svg.append('text').text(market.timeLabel(i)).attr('x', xEdge(i)).attr('y', P.axis).attr('text-anchor', 'middle').attr('class', 'dt-axis');
  }

  // Rest of the session not yet traded
  if (t < view.to) {
    const x = Math.max(P.x0, xEdge(t));
    panel.append('rect').attr('x', x).attr('y', P.y0).attr('width', P.x1 - x).attr('height', P.bottom - P.y0).attr('fill', C.untraded);
  }

  // Volume profile along the left edge
  if (on('profile')) {
    const { buy, sell } = a.profile;
    const bins = buy.length, maxV = Math.max(...buy.map((b, k) => b + sell[k])) || 1;
    for (let k = 0; k < bins; k++) {
      const p0 = a.low + ((a.high - a.low) * k) / bins, p1 = a.low + ((a.high - a.low) * (k + 1)) / bins;
      const ya = y(p1), h = Math.max(0.8, y(p0) - y(p1) - 0.8);
      if (ya < P.y0 - 1 || ya + h > P.y1 + 1) continue;
      const len = (40 * (buy[k] + sell[k])) / maxV, bw = (len * buy[k]) / ((buy[k] + sell[k]) || 1);
      svg.append('rect').attr('x', 2).attr('y', ya).attr('width', bw).attr('height', h).attr('fill', C.g).attr('opacity', 0.55);
      svg.append('rect').attr('x', 2 + bw).attr('y', ya).attr('width', Math.max(0, len - bw)).attr('height', h).attr('fill', C.r).attr('opacity', 0.55);
    }
  }

  // Order blocks: active zones, plus the latest few broken ones for context.
  if (on('zones')) {
    a.obs.filter((o, i, all) => !o.mitigated || i >= all.length - 3).forEach((ob) => {
      const isZone = d.zone && ob.idx === d.zone.idx;
      const col = ob.type === 'bull' ? C.gd : C.rd;
      const x1 = xEdge(ob.idx), x2 = xEdge(ob.mitigated ? (ob.endIdx ?? t) + 1 : t);
      if (x2 < P.x0 || x1 > P.x1) return;
      plot.append('rect').attr('x', x1).attr('y', y(ob.top)).attr('width', Math.max(2, x2 - x1)).attr('height', Math.max(2, y(ob.bottom) - y(ob.top)))
        .attr('fill', col).attr('fill-opacity', ob.mitigated ? 0.04 : isZone ? 0.16 : 0.08)
        .attr('stroke', col).attr('stroke-opacity', ob.mitigated ? 0.3 : 0.7).attr('stroke-dasharray', ob.mitigated ? '2 2' : null);
      const tag = compact ? 'OB' : zoneLabel(ob);
      plot.append('text').text(tag).attr('x', Math.min(x2, P.x1) - 3).attr('y', y(ob.top) - 3).attr('text-anchor', 'end')
        .attr('class', 'dt-tag').attr('fill', col);
    });
  }

  // Level lines: the stop side in amber, targets in the trade's colour, the far side muted.
  const labels: { text: string; y: number; color: string; box?: string }[] = [];
  if (on('levels')) {
    const target = a.bull ? C.g : C.r;
    const levelColor: Record<SRKey, string> = {
      R1: a.bull ? target : C.amber, R2: a.bull ? target : C.mute, S1: a.bull ? C.amber : target, S2: a.bull ? C.mute : target,
    };
    for (const k of ['R2', 'R1', 'S1', 'S2'] as SRKey[]) {
      const p = lv[k];
      plot.append('line').attr('x1', P.x0).attr('x2', P.x1).attr('y1', y(p)).attr('y2', y(p))
        .attr('stroke', levelColor[k]).attr('stroke-dasharray', d.projected.has(k) ? '1 3' : '4 3').attr('stroke-width', 1);
      labels.push({ text: `${k} ${fmtPx(p)}`, y: y(p), color: levelColor[k] });
    }
  }
  // Z is the plan's entry price (the zone's near edge), the same number as the trade plan.
  const zPrice = d.plan ? d.plan.z : d.zone ? (d.zone.type === 'bull' ? d.zone.top : d.zone.bottom) : null;
  if (on('zones') && zPrice != null) labels.push({ text: `Z ${fmtPx(zPrice)}`, y: y(zPrice), color: C.ink });
  if (on('vwap')) {
    const vwapNow = d.vwap[d.vwap.length - 1];
    labels.push({ text: `VWAP ${fmtPx(vwapNow)}`, y: y(vwapNow), color: C.blue });
  }

  // Liquidity lines, labelled on the left inside the plot
  liq.forEach((l) => {
    const ly = y(l.p);
    if (!inPrice(ly)) return;
    plot.append('line').attr('x1', P.x0).attr('x2', P.x1).attr('y1', ly).attr('y2', ly)
      .attr('stroke', '#a855f7').attr('stroke-width', 0.8).attr('stroke-dasharray', l.key === 'RND' ? '1 4' : '8 3').attr('opacity', 0.7);
    plot.append('text').text(`${l.key === 'RND' ? '' : l.key + ' '}${fmtPx(l.p)}`).attr('x', P.x0 + 4).attr('y', ly - 2.5)
      .attr('class', 'dt-liq').append('title').text(l.name);
  });

  // VWAP
  if (on('vwap')) {
    plot.append('path').attr('d', d3.line<number>().x((_v, i) => xAt(i)).y((v) => y(v))(d.vwap))
      .attr('fill', 'none').attr('stroke', C.blue).attr('stroke-width', 1.2).attr('stroke-dasharray', '4 3');
  }

  // Candles + volume
  const maxVol = Math.max(...visible.map(([b]) => b.v)) || 1;
  const cw = Math.max(2, Math.min(18, slot * 0.62));
  for (const [b, i] of visible) {
    const col = b.c >= b.o ? C.g : C.r, cx = xAt(i);
    plot.append('line').attr('x1', cx).attr('x2', cx).attr('y1', y(b.h)).attr('y2', y(b.l)).attr('stroke', col);
    const top = y(Math.max(b.o, b.c)), bot = y(Math.min(b.o, b.c));
    plot.append('rect').attr('x', cx - cw / 2).attr('y', top).attr('width', cw).attr('height', Math.max(1, bot - top)).attr('fill', col);
    if (!on('volume')) continue;
    const vh = ((P.v1 - P.v0) * b.v) / maxVol;
    const known = (b.bv || 0) + (b.sv || 0);
    if (a.flow && known > 0) {
      const bh = (vh * (b.bv || 0)) / known;
      panel.append('rect').attr('x', cx - cw / 2).attr('y', P.v1 - bh).attr('width', cw).attr('height', bh).attr('fill', C.g).attr('opacity', 0.45);
      panel.append('rect').attr('x', cx - cw / 2).attr('y', P.v1 - vh).attr('width', cw).attr('height', vh - bh).attr('fill', C.r).attr('opacity', 0.45);
    } else {
      panel.append('rect').attr('x', cx - cw / 2).attr('y', P.v1 - vh).attr('width', cw).attr('height', vh).attr('fill', col).attr('opacity', 0.35);
    }
  }

  // Delta panel: per-bar buy minus sell volume and its running total (cumulative delta).
  if (on('flow')) {
    const mid = (P.d0 + P.d1) / 2, half = (P.d1 - P.d0) / 2 - 2;
    svg.append('text').text('Δ').attr('x', P.x0 - 6).attr('y', mid + 3).attr('text-anchor', 'end').attr('class', 'dt-axis');
    svg.append('line').attr('x1', P.x0).attr('x2', P.x1).attr('y1', mid).attr('y2', mid).attr('stroke', C.zero);
    const f = a.flow;
    if (!f) {
      svg.append('text').text('Order flow starts once the live feed is running during market hours')
        .attr('x', (P.x0 + P.x1) / 2).attr('y', mid - 6).attr('text-anchor', 'middle').attr('class', 'dt-axis');
    } else {
      const shown = f.delta.map((v, i) => [i, v] as const).filter(([i, v]) => v !== null && i >= view.from && i < view.to) as (readonly [number, number])[];
      const maxD = Math.max(...shown.map(([, v]) => Math.abs(v))) || 1;
      for (const [i, v] of shown) {
        const h = (half * Math.abs(v)) / maxD;
        panel.append('rect').attr('x', xAt(i) - cw / 2).attr('y', v >= 0 ? mid - h : mid).attr('width', cw).attr('height', Math.max(0.5, h))
          .attr('fill', v >= 0 ? C.g : C.r).attr('opacity', 0.6);
      }
      const pts = f.cvd.map((v, i) => [i, v] as const).filter((p): p is readonly [number, number] => p[1] !== null);
      if (pts.length) {
        const maxC = Math.max(...pts.filter(([i]) => i >= view.from && i < view.to).map(([, v]) => Math.abs(v)), 1);
        const cy = (v: number) => mid - (half * Math.max(-maxC, Math.min(maxC, v))) / maxC;
        panel.append('path').attr('d', d3.line<readonly [number, number]>().x(([i]) => xAt(i)).y(([, v]) => cy(v))(pts))
          .attr('fill', 'none').attr('stroke', C.navy).attr('stroke-width', 1.3);
        const lastV = pts[pts.length - 1][1];
        svg.append('text').text(`CVD ${lastV >= 0 ? '+' : '−'}${fmtVol(Math.abs(lastV))}`).attr('x', P.x1 + 8).attr('y', cy(lastV) + 3)
          .attr('class', 'dt-label').attr('fill', C.navy);
      }
      if (f.from > view.from && f.from < view.to) panel.append('text').text(`from ${market.timeLabel(f.from)}`).attr('x', xAt(f.from)).attr('y', P.d0 + 6).attr('class', 'dt-axis');
    }
  }

  // Liquidity sweeps: a marker at the candle that poked past the level and closed back inside
  if (on('sweeps')) {
    a.sweeps.filter((w) => w.idx >= view.from && w.idx < view.to).forEach((w) => {
      const cx = xAt(w.idx), held = !w.failed, col = held ? C.amber : C.mute;
      const ty = w.type === 'bear' ? y(w.extreme) - 5 : y(w.extreme) + 5;
      const dir = w.type === 'bear' ? 1 : -1; // triangle points back toward price
      plot.append('line').attr('x1', cx - 9).attr('x2', cx + 9).attr('y1', y(w.level)).attr('y2', y(w.level)).attr('stroke', col).attr('stroke-width', 1.5);
      plot.append('path').attr('class', 'dt-sweep')
        .attr('d', `M${cx - 4.5},${ty - 4 * dir} L${cx + 4.5},${ty - 4 * dir} L${cx},${ty + 2 * dir} Z`)
        .attr('fill', held ? col : C.panel).attr('stroke', col).attr('stroke-width', 1.2)
        .append('title').text(`${held ? 'Sweep' : 'Failed sweep'} of ${w.name} ${cur}${fmtPx(w.level)} at ${market.timeLabel(w.idx)}`);
      if (!compact) {
        plot.append('text').text(`${held ? '' : '✕ '}swept ${w.key}`).attr('x', cx).attr('y', w.type === 'bear' ? ty - 7 : ty + 13)
          .attr('text-anchor', 'middle').attr('class', 'dt-sweep-label').attr('fill', col);
      }
    });
  }

  // Last price
  const ly = y(a.ltp), last = bars.length - 1;
  if (last >= view.from && last < view.to) {
    const lx = xAt(last);
    plot.append('line').attr('x1', lx).attr('x2', P.x1).attr('y1', ly).attr('y2', ly).attr('stroke', C.ink).attr('stroke-dasharray', '1 2');
    plot.append('circle').attr('cx', lx).attr('cy', ly).attr('r', 6).attr('fill', a.bull ? C.gl : C.rl).attr('stroke', a.bull ? C.g : C.r);
    plot.append('circle').attr('cx', lx).attr('cy', ly).attr('r', 2.5).attr('fill', a.bull ? C.gd : C.rd);
  }
  labels.push({ text: `${cur}${fmtPx(a.ltp)}`, y: ly, color: C.tagFg, box: C.tagBg });

  // Right-hand labels for prices in view, nudged apart so none overlap
  const shownLabels = labels.filter((l) => inPrice(l.y)).sort((p, q) => p.y - q.y);
  for (let i = 1; i < shownLabels.length; i++) shownLabels[i].y = Math.max(shownLabels[i].y, shownLabels[i - 1].y + 12);
  const over = shownLabels.length ? shownLabels[shownLabels.length - 1].y - (P.y1 + 4) : 0;
  if (over > 0) shownLabels.forEach((l) => { l.y -= over; });
  shownLabels.forEach((l) => {
    if (l.box) svg.append('rect').attr('x', P.x1 + 4).attr('y', l.y - 7).attr('width', l.text.length * 6 + 8).attr('height', 13).attr('rx', 2).attr('fill', l.box);
    svg.append('text').text(l.text).attr('x', P.x1 + 8).attr('y', l.y + 3.5).attr('class', 'dt-label').attr('fill', l.color);
  });

  if (compact) return;

  // Crosshair readout
  const readout = svg.append('text').attr('x', P.x0).attr('y', P.axis + 22).attr('class', 'dt-readout');
  const vline = svg.append('line').attr('y1', P.y0).attr('y2', P.bottom).attr('stroke', C.mute).attr('stroke-dasharray', '2 2').attr('opacity', 0);
  const show = (i: number) => {
    const b: Bar = bars[i];
    const known = (b.bv || 0) + (b.sv || 0);
    const flow = known > 0 ? `  Δ ${(b.bv || 0) >= (b.sv || 0) ? '+' : '−'}${fmtVol(Math.abs((b.bv || 0) - (b.sv || 0)))}` : '';
    readout.text(`${market.timeLabel(i)}–${market.timeLabel(i + 1)}  O ${fmtPx(b.o)}  H ${fmtPx(b.h)}  L ${fmtPx(b.l)}  C ${fmtPx(b.c)}  Vol ${fmtVol(b.v)}${flow}`);
    vline.attr('x1', xAt(i)).attr('x2', xAt(i)).attr('opacity', 1);
  };
  const hint = opts.onView ? (zoomed ? '  ·  drag to pan' : '  ·  ⌘/Ctrl + scroll or pinch to zoom') : '';
  const reset = () => { readout.text(`Hover a candle for its details${hint}`); vline.attr('opacity', 0); };
  reset();
  const barAt = (mx: number) => view.from + (mx - P.x0) / slot;
  const hit = svg.append('rect').attr('x', P.x0).attr('y', P.y0).attr('width', P.x1 - P.x0).attr('height', P.bottom - P.y0).attr('fill', 'transparent')
    .style('cursor', opts.onView && zoomed ? 'grab' : 'default')
    .on('mousemove', (ev: MouseEvent) => {
      const i = Math.floor(barAt(d3.pointer(ev)[0]));
      if (i >= 0 && i < bars.length && i < view.to) show(i); else reset();
    })
    .on('mouseleave', reset);

  const onView = opts.onView;
  if (!onView) return;
  hit.on('wheel', (ev: WheelEvent) => {
    if (!(opts.wheelZoom || ev.ctrlKey || ev.metaKey)) return;
    ev.preventDefault();
    const at = barAt(d3.pointer(ev, svgEl)[0]);
    onView(zoomView(view, ev.deltaY > 0 ? 1.2 : 1 / 1.2, at, span));
  });
  hit.on('mousedown', (ev: MouseEvent) => {
    if (!zoomed || ev.button !== 0) return;
    ev.preventDefault();
    const scale = (svgEl.getBoundingClientRect().width || W) / W; // CSS px per viewBox unit
    const x0 = ev.clientX;
    let applied = 0;
    hit.style('cursor', 'grabbing');
    const move = (e: MouseEvent) => {
      const by = Math.round(-(e.clientX - x0) / scale / slot);
      if (by !== applied) { applied = by; onView(panView(view, by, span)); }
    };
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  });
}
